import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import {
    Autocomplete,
    Box,
    Button,
    IconButton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ScopeOption } from "../fluss-builder-types";

export interface SimpleMapRow {
    target: string;
    source: string;
}

interface PayloadVariableMapperProps {
    value: SimpleMapRow[];
    onChange: (rows: SimpleMapRow[]) => void;
    payloadKeys: ScopeOption[];
    sourcePrefix?: string;
    readOnly?: boolean;
}

const PayloadVariableMapper = ({
    value,
    onChange,
    payloadKeys,
    sourcePrefix = "payload.",
    readOnly = false,
}: PayloadVariableMapperProps) => {
    const { t } = useTranslation();
    const options = useMemo(
        () => payloadKeys.map((key) => `${sourcePrefix}${key.name}`),
        [payloadKeys, sourcePrefix]
    );

    const update = (index: number, patch: Partial<SimpleMapRow>) => {
        onChange(value.map((row, rowIndex) => rowIndex === index ? { ...row, ...patch } : row));
    };

    return (
        <Stack spacing={1} data-testid="payload-variable-mapper" className="fluss-mapper">
            <Box
                sx={{ display: "grid", gap: 1 }}
            >

                {value.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ gridColumn: "1 / -1", py: 1 }}>
                        {t("fluss.node_panel.mapping.empty", {
                            defaultValue: "Add a mapping to configure this payload.",
                        })}
                    </Typography>
                )}

                {value.map((row, index) => (
                    <Box
                        key={index}
                        className="fluss-mapper__row"
                        data-testid="payload-variable-mapper-row"
                    >
                        <Box className="fluss-mapper__destination">
                            <Typography variant="caption" color="text.secondary">
                                {t("fluss.builder.field_mapper.destination", { defaultValue: "Destination field" })}
                            </Typography>
                        <TextField
                            size="small"
                            value={row.target}
                            disabled={readOnly}
                            inputProps={{
                                "aria-label": String(t("fluss.node_panel.signal.variable", {
                                    defaultValue: "Variable",
                                })),
                            }}
                            onChange={(event) => update(index, { target: event.target.value })}
                        />
                        </Box>
                        <Typography className="fluss-mapper__arrow" aria-hidden="true" color="text.secondary" textAlign="center" fontWeight={700}>
                            ←
                        </Typography>
                        <Box className="fluss-mapper__source">
                            <Typography variant="caption" color="text.secondary">
                                {t("fluss.builder.field_mapper.source", { defaultValue: "Source" })}
                            </Typography>
                        <Autocomplete
                            freeSolo
                            size="small"
                            options={options}
                            value={row.source}
                            disabled={readOnly}
                            onInputChange={(_, next, reason) => {
                                if (reason === "input" || reason === "clear") update(index, { source: next });
                            }}
                            onChange={(_, next) => update(index, {
                                source: typeof next === "string" ? next : (next ?? ""),
                            })}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputProps={{
                                        ...params.inputProps,
                                        "aria-label": String(t("fluss.node_panel.signal.payload_source", {
                                            defaultValue: "Payload source",
                                        })),
                                    }}
                                />
                            )}
                        />
                        </Box>
                        <IconButton
                            className="fluss-mapper__remove"
                            size="small"
                            color="error"
                            disabled={readOnly}
                            aria-label={String(t("fluss.builder.field_mapper.remove", {
                                defaultValue: "Remove field",
                            }))}
                            onClick={() => onChange(value.filter((_, rowIndex) => rowIndex !== index))}
                        >
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    </Box>
                ))}
            </Box>
            <Box>
                <Button
                    size="small"
                    disabled={readOnly}
                    startIcon={<AddIcon />}
                    onClick={() => onChange([...value, { target: "", source: sourcePrefix }])}
                >
                    {t("fluss.node_panel.mapping.add", { defaultValue: "Mapping" })}
                </Button>
            </Box>
        </Stack>
    );
};

export default PayloadVariableMapper;
